import { useCallback, useEffect, useState } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import useStoreProductCollections from '@/common/hooks/useStoreProductCollections/useStoreProductCollections'
import { configurationTypes, taxonomyTypeValues } from '@/configuration/pages/ConfigurationPage/constants'
import { getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import { issueExchangeStatus } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/constants'
import { exchangeOrdersAutomaticCreationValues } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/ReturnPolicyConfiguration/constants'
import { brokenDiscountHandlingValues } from '@/configuration/pages/ConfigurationPage/ReturnPolicy/ReturnPolicyConfiguration/DiscountsConfiguration/constants'
import { customerContactMethods } from '@/configuration/pages/ConfigurationPage/ReturnsPortalConfiguration/ReturnsPortalConfigurationPage/CustomerContactMethod/constants'
import configurationServices from '@/configuration/services/services'

import { servicesByConfigurationMode } from './constants'
import { parseTime } from './helpers'

const useReturnPolicyConfiguration = ({ storeId, merchantId, returnPortalId, configurationMode }) => {
  const services = servicesByConfigurationMode[configurationMode || configurationTypes.returnsPortal]
  const entityId = getEntityIdByConfigurationMode({ configurationMode, merchantId, returnPortalId })
  const isReturnsPortalMode = configurationMode === configurationTypes.returnsPortal

  const {
    data,
    loading,
    fetchData: refetchConfigurationPreferences,
  } = useFetch(services.getPreferences, {
    doInitialRequest: true,
    params: entityId,
  })

  const { productCategories, fetchingProductCategories } = useStoreProductCategories({
    storeId,
    doInitialRequest: isReturnsPortalMode,
  })
  const { productCollections, fetchingProductCollections } = useStoreProductCollections({
    storeId,
    doInitialRequest: isReturnsPortalMode,
  })
  const { data: productTypes, loading: fetchingProductTypes } = useFetch(
    () => configurationServices.getStoreProductTypes({ storeId }),
    {
      doInitialRequest: isReturnsPortalMode,
    }
  )

  const { fetchData: savePreferences, loading: savingPreferences } = useFetch(services.editPreferences, {
    doInitialRequest: false,
  })

  const [allowReturns, setAllowReturns] = useState('')
  const [maxDurationForRefund, setMaxDurationForRefund] = useState('')
  const [shippingIncluded, setShippingIncluded] = useState('')
  const [allowExchanges, setAllowExchanges] = useState('')
  const [maxDurationForExchanges, setMaxDurationForExchanges] = useState('')
  const [allowedExchangeMethods, setAllowedExchangeMethods] = useState('')
  const [exchangeOrdersAutomaticCreation, setExchangeOrdersAutomaticCreation] = useState('')
  const [allowInstantExchanges, setAllowInstantExchanges] = useState('')
  const [instantExchangeOrdersAutomaticCreation, setInstantExchangeOrdersAutomaticCreation] = useState('')
  const [allowEvenExchangesCatalogPrice, setAllowEvenExchangesCatalogPrice] = useState('')
  const [issueExchangeAtStatus, setIssueExchangeAtStatus] = useState('')
  const [exchangeOrdersSendProviderNotification, setExchangeOrdersSendProviderNotification] = useState('')
  const [instantExchangeOrdersSendProviderNotification, setInstantExchangeOrdersSendProviderNotification] = useState('')
  const [productVariantExchangeRecommendationType, setProductVariantExchangeRecommendationType] = useState('')
  const [productVariantExchangeRecommendationValue, setProductVariantExchangeRecommendationValue] = useState('')
  const [productVariantExchangeTaxonomyType, setProductVariantExchangeTaxonomyType] = useState('')
  const [productVariantExchangeTaxonomyDisplay, setProductVariantExchangeTaxonomyDisplay] = useState('')
  const [productVariantExchangeTaxonomyIds, setProductVariantExchangeTaxonomyIds] = useState([])
  const [returnsPolicyUrl, setReturnsPolicyUrl] = useState('')
  const [exchangeErrorResolution, setExchangeErrorResolution] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [allowReturnCancelDeadline, setAllowReturnCancelDeadline] = useState(false)
  const [returnCancelDeadlineDays, setReturnCancelDeadlineDays] = useState(null)
  const [helpCenterUrl, setHelpCenterUrl] = useState('')
  const [manageRestock, setManageRestock] = useState(false)
  const [refundReferenceDate, setRefundReferenceDate] = useState('')
  const [exchangeReferenceDate, setExchangeReferenceDate] = useState('')
  const [salesOrdersDataActivated, setSalesOrdersDataActivated] = useState(false)
  const [currentMode, setCurrentMode] = useState(configurationMode)
  const [customerContactMethod, setCustomerContactMethod] = useState('')
  const [giftCardAllowed, setGiftCardAllowed] = useState(false)
  const [storeCreditAllowed, setStoreCreditAllowed] = useState(false)
  const [bundleReturnPolicy, setBundleReturnPolicy] = useState(null)
  const [bundleAllowedRequests, setBundleAllowedRequests] = useState(null)
  const [buyXGetYReturnPolicy, setBuyXGetYReturnPolicy] = useState(null)
  const [brokenDiscountHandling, setBrokenDiscountHandling] = useState(brokenDiscountHandlingValues.noOp)
  const [brokenDiscountBonusEnabled, setBrokenDiscountBonusEnabled] = useState(false)
  const [brokenDiscountCustomMessageEnabled, setBrokenDiscountCustomMessageEnabled] = useState(false)
  const [brokenDiscountCustomMessage, setBrokenDiscountCustomMessage] = useState({})
  const [exchangePaymentExpiration, setExchangePaymentExpiration] = useState(null)
  const [instantExchangePaymentExpiration, setInstantExchangePaymentExpiration] = useState(null)
  const [allowsGiftReturns, setAllowsGiftReturns] = useState(false)
  const [skipIdentifierValidation, setSkipIdentifierValidation] = useState(false)
  const [allowsOrderRecovery, setAllowsOrderRecovery] = useState(false)
  const [skipSingleRefundMethodStep, setSkipSingleRefundMethodStep] = useState(false)

  const setInitValues = useCallback(() => {
    if (data) {
      setAllowReturns(data?.allows_refunds)
      setMaxDurationForRefund(parseTime(data?.max_duration_for_refund))
      setShippingIncluded(data?.shipping_included)
      setAllowExchanges(data?.allows_exchanges)
      setMaxDurationForExchanges(parseTime(data?.max_duration_for_exchange))
      setAllowedExchangeMethods(data?.allowed_exchange_methods || [])
      setExchangeOrdersAutomaticCreation(
        data?.exchange_orders_automatic_creation
          ? exchangeOrdersAutomaticCreationValues.automatic
          : exchangeOrdersAutomaticCreationValues.manual
      )
      setAllowInstantExchanges(data?.allows_instant_exchanges)
      setInstantExchangeOrdersAutomaticCreation(
        data?.instant_exchange_orders_automatic_creation
          ? exchangeOrdersAutomaticCreationValues.automatic
          : exchangeOrdersAutomaticCreationValues.manual
      )
      setAllowEvenExchangesCatalogPrice(data?.allow_even_exchanges_catalog_price || false)
      setIssueExchangeAtStatus(data?.issue_exchange_at_status || issueExchangeStatus.accepted)
      setExchangeOrdersSendProviderNotification(data?.exchange_orders_send_provider_notification || false)
      setInstantExchangeOrdersSendProviderNotification(
        data?.instant_exchange_orders_send_provider_notification || false
      )
      setProductVariantExchangeRecommendationType(data?.product_variant_exchange_recommendation_type)
      setProductVariantExchangeRecommendationValue(data?.product_variant_exchange_recommendation_value)
      setProductVariantExchangeTaxonomyType(data?.product_variant_exchange_taxonomy_type || taxonomyTypeValues.category)
      setProductVariantExchangeTaxonomyDisplay(data?.product_variant_exchange_taxonomy_display)
      setProductVariantExchangeTaxonomyIds(data?.product_variant_exchange_taxonomy_ids || [])
      setReturnsPolicyUrl(data?.returns_policy_url || '')
      setExchangeErrorResolution(data?.exchange_error_resolution || '')
      setHasChanges(false)
      setAllowReturnCancelDeadline(data?.return_cancel_deadline_days || false)
      setReturnCancelDeadlineDays(data?.return_cancel_deadline_days || null)
      setHelpCenterUrl(data?.help_center_url || '')
      setManageRestock(data?.manage_restock || false)
      setRefundReferenceDate(data?.refund_reference_date || '')
      setExchangeReferenceDate(data?.exchange_reference_date || '')
      setSalesOrdersDataActivated(data?.sales_orders_data_activated || false)
      setCustomerContactMethod(data?.contact_method || customerContactMethods.email)
      setGiftCardAllowed(data?.gift_card_allowed || false)
      setStoreCreditAllowed(data?.store_credit_allowed || false)
      setBundleReturnPolicy(data?.bundle_return_policy || null)
      setBundleAllowedRequests(data?.bundle_allowed_requests || null)
      setBuyXGetYReturnPolicy(data?.buyxgety_return_policy || null)
      setBrokenDiscountHandling(data?.broken_discount_handling || brokenDiscountHandlingValues.noOp)
      setBrokenDiscountBonusEnabled(data?.broken_discount_bonus_enabled || false)
      setBrokenDiscountCustomMessageEnabled(data?.broken_discount_custom_message_enabled || false)
      setBrokenDiscountCustomMessage(data?.broken_discount_custom_message || {})
      setExchangePaymentExpiration(data?.exchange_payment_expiration_delta || null)
      setInstantExchangePaymentExpiration(data?.instant_exchange_payment_expiration_delta || null)
      setAllowsGiftReturns(data?.allows_gift_returns || false)
      setSkipIdentifierValidation(data?.skip_identifier_validation || false)
      setAllowsOrderRecovery(data?.allows_order_recovery || false)
      setSkipSingleRefundMethodStep(data?.skip_single_refund_method_step || false)
    }
  }, [data])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  useEffect(() => {
    if (currentMode && configurationMode !== currentMode) {
      refetchConfigurationPreferences(entityId)
    }
    setCurrentMode(configurationMode)
  }, [configurationMode, currentMode, entityId, refetchConfigurationPreferences])

  const revertChanges = () => {
    setInitValues()
  }

  return {
    configurationPreferences: data,
    loadingConfigurationPreferences: loading,
    refetchConfigurationPreferences,
    allowReturns: allowReturns || false,
    setAllowReturns: params => {
      setHasChanges(true)
      setAllowReturns(params)
    },
    maxDurationForRefund: maxDurationForRefund || '',
    setMaxDurationForRefund: params => {
      setHasChanges(true)
      setMaxDurationForRefund(params)
    },
    shippingIncluded: shippingIncluded || false,
    setShippingIncluded: params => {
      setHasChanges(true)
      setShippingIncluded(params)
    },
    allowExchanges: allowExchanges || false,
    setAllowExchanges: params => {
      setHasChanges(true)
      setAllowExchanges(params)
    },
    maxDurationForExchanges: maxDurationForExchanges || '',
    setMaxDurationForExchanges: params => {
      setHasChanges(true)
      setMaxDurationForExchanges(params)
    },
    allowedExchangeMethods: allowedExchangeMethods || [],
    setAllowedExchangeMethods: params => {
      setHasChanges(true)
      setAllowedExchangeMethods(params)
    },
    exchangeOrdersAutomaticCreation: exchangeOrdersAutomaticCreation || exchangeOrdersAutomaticCreationValues.manual,
    setExchangeOrdersAutomaticCreation: params => {
      setHasChanges(true)
      setExchangeOrdersAutomaticCreation(params)
    },
    allowInstantExchanges: allowInstantExchanges || false,
    setAllowInstantExchanges: params => {
      setHasChanges(true)
      setAllowInstantExchanges(params)
    },
    instantExchangeOrdersAutomaticCreation:
      instantExchangeOrdersAutomaticCreation || exchangeOrdersAutomaticCreationValues.manual,
    setInstantExchangeOrdersAutomaticCreation: params => {
      setHasChanges(true)
      setInstantExchangeOrdersAutomaticCreation(params)
    },
    allowEvenExchangesCatalogPrice: allowEvenExchangesCatalogPrice || false,
    setAllowEvenExchangesCatalogPrice: params => {
      setHasChanges(true)
      setAllowEvenExchangesCatalogPrice(params)
    },
    issueExchangeAtStatus,
    setIssueExchangeAtStatus: params => {
      setHasChanges(true)
      setIssueExchangeAtStatus(params)
    },
    exchangeOrdersSendProviderNotification: exchangeOrdersSendProviderNotification || false,
    setExchangeOrdersSendProviderNotification: params => {
      setHasChanges(true)
      setExchangeOrdersSendProviderNotification(params)
    },
    instantExchangeOrdersSendProviderNotification: instantExchangeOrdersSendProviderNotification || false,
    setInstantExchangeOrdersSendProviderNotification: params => {
      setHasChanges(true)
      setInstantExchangeOrdersSendProviderNotification(params)
    },
    productVariantExchangeRecommendationType: productVariantExchangeRecommendationType || '',
    setProductVariantExchangeRecommendationType: params => {
      setHasChanges(true)
      setProductVariantExchangeRecommendationType(params)
    },
    productVariantExchangeRecommendationValue: productVariantExchangeRecommendationValue || '',
    setProductVariantExchangeRecommendationValue: params => {
      setHasChanges(true)
      setProductVariantExchangeRecommendationValue(params)
    },
    productVariantExchangeTaxonomyType: productVariantExchangeTaxonomyType || '',
    setProductVariantExchangeTaxonomyType: params => {
      setHasChanges(true)
      setProductVariantExchangeTaxonomyType(params)
      setProductVariantExchangeTaxonomyIds([])
    },
    productVariantExchangeTaxonomyDisplay: productVariantExchangeTaxonomyDisplay || '',
    setProductVariantExchangeTaxonomyDisplay: params => {
      setHasChanges(true)
      setProductVariantExchangeTaxonomyDisplay(params)
      setProductVariantExchangeTaxonomyIds([])
    },
    productVariantExchangeTaxonomyIds: productVariantExchangeTaxonomyIds || [],
    setProductVariantExchangeTaxonomyIds: params => {
      setHasChanges(true)
      setProductVariantExchangeTaxonomyIds(params)
    },
    returnsPolicyUrl: returnsPolicyUrl || '',
    setReturnsPolicyUrl: params => {
      setHasChanges(true)
      setReturnsPolicyUrl(params)
    },
    productCategories: productCategories || [],
    productCollections: productCollections || [],
    productTypes: productTypes || [],
    gettingProductCategories: fetchingProductCategories,
    gettingProductCollections: fetchingProductCollections,
    fetchingProductTypes,
    savePreferences,
    savingPreferences,
    exchangeErrorResolution,
    setExchangeErrorResolution: params => {
      setHasChanges(true)
      setExchangeErrorResolution(params)
    },
    hasChanges,
    setHasChanges,
    revertChanges,
    returnCancelDeadlineDays,
    setReturnCancelDeadlineDays: params => {
      setHasChanges(true)
      setReturnCancelDeadlineDays(params)
    },
    availableIssueExchangeStatus: data?.available_issue_exchange_status,
    availableTaxonomyTypes: data?.available_taxonomy_types || [],
    availableRecommendationTypes: data?.available_recommendation_types || [],
    helpCenterUrl,
    setHelpCenterUrl: params => {
      setHasChanges(true)
      setHelpCenterUrl(params)
    },
    manageRestock,
    setManageRestock: params => {
      setManageRestock(params)
      setHasChanges(true)
    },
    refundReferenceDate,
    setRefundReferenceDate: params => {
      setRefundReferenceDate(params)
      setHasChanges(true)
    },
    exchangeReferenceDate,
    setExchangeReferenceDate: params => {
      setExchangeReferenceDate(params)
      setHasChanges(true)
    },
    salesOrdersDataActivated,
    setSalesOrdersDataActivated: params => {
      setSalesOrdersDataActivated(params)
      setHasChanges(true)
    },
    allowReturnCancelDeadline,
    setAllowReturnCancelDeadline: params => {
      setAllowReturnCancelDeadline(params)
      setHasChanges(true)
    },
    entityId,
    customerContactMethod,
    setCustomerContactMethod: params => {
      setHasChanges(true)
      setCustomerContactMethod(params)
    },
    giftCardAllowed,
    setGiftCardAllowed: params => {
      setHasChanges(true)
      setGiftCardAllowed(params)
    },
    storeCreditAllowed,
    setStoreCreditAllowed: params => {
      setHasChanges(true)
      setStoreCreditAllowed(params)
    },
    bundleReturnPolicy,
    setBundleReturnPolicy: params => {
      setHasChanges(true)
      setBundleReturnPolicy(params)
    },
    bundleAllowedRequests,
    setBundleAllowedRequests: params => {
      setHasChanges(true)
      setBundleAllowedRequests(params)
    },
    buyXGetYReturnPolicy,
    setBuyXGetYReturnPolicy: params => {
      setHasChanges(true)
      setBuyXGetYReturnPolicy(params)
    },
    brokenDiscountHandling,
    setBrokenDiscountHandling: params => {
      setHasChanges(true)
      setBrokenDiscountHandling(params)
    },
    allowsBrokenDiscountHandling: data?.allows_broken_discount_handling || false,
    brokenDiscountBonusEnabled,
    setBrokenDiscountBonusEnabled: params => {
      setHasChanges(true)
      setBrokenDiscountBonusEnabled(params)
    },
    brokenDiscountCustomMessageEnabled,
    setBrokenDiscountCustomMessageEnabled: params => {
      setHasChanges(true)
      setBrokenDiscountCustomMessageEnabled(params)
    },
    brokenDiscountCustomMessage,
    setBrokenDiscountCustomMessage: params => {
      setHasChanges(true)
      setBrokenDiscountCustomMessage(params)
    },
    exchangePaymentExpiration,
    setExchangePaymentExpiration: params => {
      setHasChanges(true)
      setExchangePaymentExpiration(params)
    },
    instantExchangePaymentExpiration,
    setInstantExchangePaymentExpiration: params => {
      setHasChanges(true)
      setInstantExchangePaymentExpiration(params)
    },
    allowsGiftReturns,
    setAllowsGiftReturns: params => {
      setHasChanges(true)
      setAllowsGiftReturns(params)
    },
    skipIdentifierValidation,
    setSkipIdentifierValidation: params => {
      setHasChanges(true)
      setSkipIdentifierValidation(params)
    },
    allowsOrderRecovery,
    setAllowsOrderRecovery: params => {
      setHasChanges(true)
      setAllowsOrderRecovery(params)
    },
    skipSingleRefundMethodStep,
    setSkipSingleRefundMethodStep: params => {
      setHasChanges(true)
      setSkipSingleRefundMethodStep(params)
    },
    hasOrderRecoveryEmailTemplate: data?.has_order_recovery_email_template || false,
  }
}

export default useReturnPolicyConfiguration
